import { Button, Calendar, Popover, Space } from 'antd'
import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import { observer } from 'mobx-react-lite'
import PropTypes from 'prop-types'
import StatusTag from './StatusTag'
import { newSprint, requireSprintById } from '../../stores/sprints/actions'
import dayjs from 'dayjs'
import { translate } from '../../cls/TranslationContext'

/**
 * CustomCalendar component to render a calendar with sprints and their statuses.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.store - The MobX store containing sprint data.
 * @returns {JSX.Element} - The rendered CustomCalendar component.
 */
const CustomCalendar = observer(({ store, onChange, onPanelChange }) => {
	const navigate = useNavigate()
	const { ids } = store

	const [isPopoverVisible, setIsPopoverVisible] = useState(false)
	const [selectedDate, setSelectedDate] = useState(null)

	/**
	 * Handles the selection of a date in the calendar.
	 *
	 * This function differentiates between selections made directly from the calendar
	 * and other sources. If the selection is from the calendar, it updates the selected date
	 * and displays a popover for additional actions. Otherwise, it calls the provided
	 * onChange function with the selected date.
	 *
	 * @param {dayjs.Dayjs} date - The selected date.
	 * @param {Object} info - Additional information about the selection event.
	 * @param {string} info.source - The source of the selection, which can be 'date' or other types.
	 */
	const handleSelect = (date, info) => {
		if (info?.source !== 'date') {
			onChange(date)
		} else {
			setSelectedDate(date)
			setIsPopoverVisible(true)
		}
	}

	/**
	 * Get the date associated with a sprint.
	 * @param {Object} sprint - The sprint object.
	 * @returns {number} - The timestamp of the sprint date.
	 */
	const getSprintDate = (sprint) => {
		const { recurrence, createdTs } = sprint || {}

		if (recurrence?.type) {
			return recurrence.type === 'deadline'
				? recurrence?.startEnd[0]
				: recurrence.next
		}

		return createdTs
	}

	/**
	 * Get the number of backlog items for a specific month.
	 * @param {dayjs.Dayjs} value - The month to get data for.
	 * @returns {number|null} - The number of backlog items or null if none.
	 */
	const getMonthData = (value) => {
		const month = value.month()
		let backlogCount = 0

		ids.forEach((id) => {
			const sprint = requireSprintById(id)
			const sprintDate = dayjs.unix(getSprintDate(sprint))

			if (sprintDate.month() === month) {
				backlogCount += 1
			}
		})

		return backlogCount || 0
	}

	/**
	 * Get a list of sprints for a specific date.
	 * @param {dayjs.Dayjs} value - The date to get list data for.
	 * @returns {Array} - An array of sprints for the specified date.
	 */
	const getListData = (value) => {
		const listData = []

		ids.forEach((id) => {
			const sprint = requireSprintById(id)
			const sprintDate = dayjs.unix(getSprintDate(sprint))

			if (sprintDate.startOf('day').isSame(value.startOf('day'))) {
				listData.push({
					id: sprint.id,
					name: sprint.name,
					progress: sprint.progress
				})
			}
		})
		return listData
	}

	/**
	 * Render the month cell with backlog data.
	 * @param {dayjs.Dayjs} value - The month to render.
	 * @returns {JSX.Element|null} - The rendered month cell or null.
	 */
	const monthCellRender = (value) => {
		const num = getMonthData(value)
		return num ? (
			<div className="notes-month">
				<section>{num}</section>
				<span>{translate('_sprint.multiple')}</span>
			</div>
		) : null
	}

	/**
	 * Render the date cell with sprint data.
	 * @param {dayjs.Dayjs} value - The date to render.
	 * @returns {JSX.Element} - The rendered date cell.
	 */
	const dateCellRender = (value) => {
		const listData = getListData(value)

		return (
			<Space style={{ width: '100%' }} direction="vertical">
				{listData.map((sprint) => (
					<Popover
						key={sprint.id}
						style={{
							width: '100px'
						}}
						content={
							<Space style={{ width: '100%' }}>
								<StatusTag
									isShort
									tag={sprint.progress}
									style={{
										marginRight: 0,
										padding: '0 3px'
									}}
								/>
								{sprint.name}
							</Space>
						}
					>
						<Space style={{ width: '100%' }}>
							<StatusTag
								isShort
								tag={sprint.progress}
								style={{
									marginRight: 0,
									padding: '0 3px'
								}}
							/>
							<Link
								to={`sprints/${sprint.id}/view`}
								style={{
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									maxWidth: '150px'
								}}
							>
								{sprint.name.length > 20
									? `${sprint.name.slice(0, 20)}...`
									: sprint.name}
							</Link>
						</Space>
					</Popover>
				))}
			</Space>
		)
	}

	/**
	 * Render the calendar cell based on type.
	 * @param {dayjs.Dayjs} current - The current cell being rendered.
	 * @param {Object} info - Information about the cell type.
	 * @returns {JSX.Element} - The rendered cell.
	 */
	const cellRender = (current, info) => {
		if (info.type === 'date') return dateCellRender(current)
		if (info.type === 'month') return monthCellRender(current)
		return info.originNode
	}

	return (
		<div id="calendar-container" className="we-custom-calendar">
			<div id="add-new-sprint-pop-over" style={{ position: 'absolute' }}>
				{isPopoverVisible && selectedDate && (
					<Popover
						className="add-new-sprint-pop-over"
						content={
							<Space
								style={{ width: '100%' }}
								direction="vertical"
							>
								<Button
									id="target-filter-addnew"
									onClick={() => {
										const newId = uuidv4()
										newSprint(newId).then(() => {
											navigate(
												`/sprints/${newId}/edit?deadline=${selectedDate.unix()}`
											)
										})
									}}
								>
									{translate('single.sprints.add-for')}{' '}
									{selectedDate.format('DD/MM/YYYY')}
								</Button>
							</Space>
						}
						open={isPopoverVisible}
						onOpenChange={(visible) => setIsPopoverVisible(visible)}
						trigger="click"
						placement="bottomLeft"
						getPopupContainer={() =>
							document.getElementById('calendar-container')
						}
					/>
				)}
			</div>
			<Calendar
				cellRender={cellRender}
				onPanelChange={onPanelChange}
				onSelect={(date, info) => {
					handleSelect(date, info)
					if (info.source === 'date') {
						const calendarCell = document.querySelector(
							`td[title="${date.format('YYYY-MM-DD')}"]`
						)
						if (calendarCell) {
							const rect = calendarCell.getBoundingClientRect()
							const popover = document.querySelector(
								'#add-new-sprint-pop-over'
							)
							if (popover) {
								popover.style.top = `${
									rect.top + window.scrollY + 15
								}px`
								popover.style.left = `${
									rect.left + window.scrollX
								}px`
							}
						}
					}
				}}
			/>
		</div>
	)
})

CustomCalendar.propTypes = {
	store: PropTypes.object.isRequired,
	onChange: PropTypes.func.isRequired,
	onPanelChange: PropTypes.func.isRequired
}

export default CustomCalendar
